import React, { useEffect, useMemo, useState } from 'react';

import { apiFetch } from './api';

const RESULT_LABELS = {
  applicable: 'Kan appliceras',
  already_applied: 'Redan applicerad',
  conflict_or_customized: 'Konflikt / kundanpassad',
  failed_validation: 'Valideringsfel',
  applied: 'Applicerad',
  pull_request_created: 'PR skapad'
};

const RESULT_CLASSNAMES = {
  applicable: 'workflow-status__pill workflow-status__pill--info',
  already_applied: 'workflow-status__pill workflow-status__pill--muted',
  conflict_or_customized: 'workflow-status__pill workflow-status__pill--error',
  failed_validation: 'workflow-status__pill workflow-status__pill--error',
  applied: 'workflow-status__pill workflow-status__pill--success',
  pull_request_created: 'workflow-status__pill workflow-status__pill--warning'
};

function summarizeResults(results) {
  return results.reduce((acc, result) => {
    acc[result.status] = (acc[result.status] || 0) + 1;
    return acc;
  }, {});
}

function resultLabel(status) {
  return RESULT_LABELS[status] || status;
}

function resultClassName(status) {
  return RESULT_CLASSNAMES[status] || 'workflow-status__pill workflow-status__pill--muted';
}

function statusOrder(status) {
  return ['applied', 'pull_request_created', 'applicable', 'already_applied', 'conflict_or_customized', 'failed_validation'].indexOf(status);
}

function PullRequestMeta({ pullRequest }) {
  if (!pullRequest?.url) {
    return null;
  }

  return (
    <p className="muted-text">
      PR: <a href={pullRequest.url} target="_blank" rel="noreferrer">#{pullRequest.number || 'öppna'}</a>
      {pullRequest.branch ? <> · Branch: <code>{pullRequest.branch}</code></> : null}
    </p>
  );
}

function PatchResultSummary({ results }) {
  const summary = useMemo(() => summarizeResults(results), [results]);

  return (
    <div className="patch-summary-grid">
      {Object.entries(summary)
        .sort((left, right) => statusOrder(left[0]) - statusOrder(right[0]))
        .map(([status, count]) => (
          <div key={status} className="patch-summary-card shell-subcard">
            <span className={resultClassName(status)}>{resultLabel(status)}</span>
            <strong>{count}</strong>
          </div>
        ))}
    </div>
  );
}

function PatchResultItem({ result, onRemove }) {
  return (
    <details className="patch-accordion patch-result-card" open>
      <summary className="patch-accordion__summary">
        <div>
          <h3>{result.repoName}</h3>
          <p className="workflow-run-card__meta">
            <span>{result.siteId}</span>
            {result.commitSha ? <span>Commit {result.commitSha.slice(0, 7)}</span> : null}
          </p>
        </div>
        <span className={resultClassName(result.status)}>{resultLabel(result.status)}</span>
      </summary>
      <div className="patch-accordion__content">
        <p className="muted-text">{result.reason}</p>
        <PullRequestMeta pullRequest={result.pullRequest} />
        {result.changedFiles?.length ? (
          <div className="patch-file-list">
            {result.changedFiles.map((filePath) => <code key={filePath}>{filePath}</code>)}
          </div>
        ) : null}
        <div className="patch-accordion__actions">
          <button type="button" className="button button--ghost button--small" onClick={onRemove}>
            Ta bort
          </button>
        </div>
      </div>
    </details>
  );
}

function PatchHistoryItem({ run, onDelete, busy }) {
  return (
    <details className="patch-accordion patch-history-card">
      <summary className="patch-accordion__summary">
        <div>
          <h3>{run.site?.displayName || run.site?.repoName || run.siteId}</h3>
          <p className="workflow-run-card__meta">
            <span>{run.patchId}</span>
            <span>{new Date(run.createdAt).toLocaleString('sv-SE')}</span>
          </p>
        </div>
        <span className={resultClassName(run.status)}>{resultLabel(run.status)}</span>
      </summary>
      <div className="patch-accordion__content">
        <p className="muted-text">{run.reason}</p>
        {run.commitSha ? <p className="muted-text">Commit: {run.commitSha.slice(0, 7)}</p> : null}
        <PullRequestMeta pullRequest={run.pullRequest} />
        {run.changedFiles?.length ? (
          <div className="patch-file-list">
            {run.changedFiles.map((filePath) => <code key={filePath}>{filePath}</code>)}
          </div>
        ) : null}
        <div className="patch-accordion__actions">
          <button type="button" className="button button--ghost button--small" disabled={busy} onClick={onDelete}>
            {busy ? 'Tar bort...' : 'Ta bort'}
          </button>
        </div>
      </div>
    </details>
  );
}

function PatchHistory({ runs, deletingRunId, onDelete }) {
  if (!runs.length) {
    return <p className="workflow-status__empty">Ingen patchhistorik ännu.</p>;
  }

  return (
    <div className="patch-history-list">
      {runs.map((run) => (
        <PatchHistoryItem
          key={run.sitePatchRunId}
          run={run}
          busy={deletingRunId === run.sitePatchRunId}
          onDelete={() => onDelete(run.sitePatchRunId)}
        />
      ))}
    </div>
  );
}

export default function PatchManager({ sites, accessToken }) {
  const [patches, setPatches] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPatchId, setSelectedPatchId] = useState('');
  const [targetMode, setTargetMode] = useState('all');
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [deletingRunId, setDeletingRunId] = useState('');

  const activeSites = useMemo(
    () => sites.filter((site) => site.status === 'active'),
    [sites]
  );

  const selectedPatch = useMemo(
    () => patches.find((patch) => patch.patchId === selectedPatchId) || null,
    [patches, selectedPatchId]
  );

  const selectionKey = useMemo(
    () => JSON.stringify({ patchId: selectedPatchId, targetMode, selectedSiteIds: [...selectedSiteIds].sort() }),
    [selectedPatchId, targetMode, selectedSiteIds]
  );

  useEffect(() => {
    let active = true;

    async function loadData() {
      setLoading(true);
      setError('');

      try {
        const [patchPayload, runPayload] = await Promise.all([
          apiFetch('/admin/patches', { accessToken }),
          apiFetch('/admin/patches/runs?limit=12', { accessToken })
        ]);

        if (!active) {
          return;
        }

        const nextPatches = patchPayload.data || [];
        setPatches(nextPatches);
        setRuns(runPayload.data || []);
        setSelectedPatchId((current) => current || nextPatches[0]?.patchId || '');
      } catch (requestError) {
        if (active) {
          setError(requestError.message || 'Kunde inte läsa patchdata.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      active = false;
    };
  }, [accessToken]);

  useEffect(() => {
    setPreviewData(null);
    setActionError('');
    setCopyMessage('');
  }, [selectionKey]);

  async function refreshRuns() {
    const runPayload = await apiFetch('/admin/patches/runs?limit=12', { accessToken });
    setRuns(runPayload.data || []);
  }

  function toggleSite(siteId) {
    setSelectedSiteIds((current) => (
      current.includes(siteId)
        ? current.filter((id) => id !== siteId)
        : [...current, siteId]
    ));
  }

  function removePreviewResult(siteId, patchId) {
    setPreviewData((current) => {
      if (!current) {
        return current;
      }

      const nextResults = (current.results || []).filter((result) => !(result.siteId === siteId && result.patchId === patchId));
      return {
        ...current,
        results: nextResults
      };
    });
  }

  async function deletePatchRun(sitePatchRunId) {
    setDeletingRunId(sitePatchRunId);
    setActionError('');

    try {
      await apiFetch(`/admin/patches/runs/${sitePatchRunId}`, {
        accessToken,
        method: 'DELETE'
      });
      setRuns((current) => current.filter((run) => run.sitePatchRunId !== sitePatchRunId));
    } catch (requestError) {
      setActionError(requestError.message || 'Kunde inte ta bort patchkörningen.');
    } finally {
      setDeletingRunId('');
    }
  }

  async function runPatch(action) {
    if (!selectedPatchId) {
      return;
    }

    const setBusy = action === 'preview' ? setPreviewBusy : setApplyBusy;
    setBusy(true);
    setActionError('');
    setCopyMessage('');

    try {
      const payload = await apiFetch(`/admin/patches/${selectedPatchId}/${action}`, {
        accessToken,
        method: 'POST',
        body: {
          targetMode,
          siteIds: targetMode === 'selected' ? selectedSiteIds : []
        }
      });

      setPreviewData(payload.data);
      await refreshRuns();
    } catch (requestError) {
      setActionError(requestError.message || 'Patchkörningen misslyckades.');
    } finally {
      setBusy(false);
    }
  }

  async function copyFailingSites() {
    if (!previewData?.results?.length) {
      return;
    }

    const failingSites = previewData.results
      .filter((result) => result.status === 'conflict_or_customized' || result.status === 'failed_validation')
      .map((result) => `${result.repoName} (${result.siteId}) - ${result.reason}`)
      .join('\n');

    if (!failingSites) {
      setCopyMessage('Det finns inga felande sajter att kopiera.');
      return;
    }

    try {
      await navigator.clipboard.writeText(failingSites);
      setCopyMessage('Felande sajter kopierades till urklipp.');
    } catch (copyError) {
      setCopyMessage('Kunde inte kopiera till urklipp.');
    }
  }

  const canRunSelected = targetMode === 'all' || selectedSiteIds.length > 0;
  const results = previewData?.results || [];
  const hasConflictResults = results.some((result) => result.status === 'conflict_or_customized');

  return (
    <section className="shell-card patch-manager">
      <div className="patch-manager__header">
        <div>
          <p className="eyebrow">Templatepatchar</p>
          <h2>Massuppdatera webbplatser</h2>
          <p className="muted-text">Kör versionerade patchar mot många sajter samtidigt med preview innan du applicerar.</p>
        </div>
      </div>

      {loading ? <p>Laddar patchar...</p> : null}
      {!loading && error ? <p className="feedback feedback--error">{error}</p> : null}

      {!loading && !error ? (
        <>
          <div className="patch-manager__grid">
            <label className="field">
              <span>Patch</span>
              <select value={selectedPatchId} onChange={(event) => setSelectedPatchId(event.target.value)}>
                {patches.map((patch) => (
                  <option key={patch.patchId} value={patch.patchId}>
                    {patch.title}
                  </option>
                ))}
              </select>
            </label>

            <div className="patch-target-group shell-subcard">
              <strong>Målgrupp</strong>
              <label className="toggle-field">
                <input
                  type="radio"
                  name="patch-target-mode"
                  checked={targetMode === 'all'}
                  onChange={() => setTargetMode('all')}
                />
                <span>Alla aktiva sajter ({activeSites.length})</span>
              </label>
              <label className="toggle-field">
                <input
                  type="radio"
                  name="patch-target-mode"
                  checked={targetMode === 'selected'}
                  onChange={() => setTargetMode('selected')}
                />
                <span>Valda sajter</span>
              </label>
            </div>
          </div>

          {selectedPatch ? (
            <div className="patch-details shell-subcard">
              <div className="patch-details__header">
                <span className="status-pill">{selectedPatch.patchId}</span>
                <span className="status-pill">Risk: {selectedPatch.riskLevel}</span>
                <span className="status-pill">{selectedPatch.createdAt}</span>
              </div>
              <p>{selectedPatch.description}</p>
              <p className="muted-text">{selectedPatch.warning}</p>
              <div className="patch-file-list">
                {selectedPatch.allowedFiles.map((filePath) => <code key={filePath}>{filePath}</code>)}
              </div>
            </div>
          ) : null}

          {targetMode === 'selected' ? (
            <div className="patch-site-picker shell-subcard">
              <div className="patch-site-picker__header">
                <strong>Välj sajter</strong>
                <button
                  type="button"
                  className="button button--ghost button--small"
                  onClick={() => setSelectedSiteIds(activeSites.map((site) => site.site_id))}
                >
                  Markera alla aktiva
                </button>
              </div>
              <div className="patch-site-picker__list">
                {activeSites.map((site) => (
                  <label key={site.site_id} className="patch-site-option">
                    <input
                      type="checkbox"
                      checked={selectedSiteIds.includes(site.site_id)}
                      onChange={() => toggleSite(site.site_id)}
                    />
                    <span>
                      <strong>{site.display_name}</strong>
                      <small>{site.repo_owner}/{site.repo_name}</small>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          <div className="patch-actions">
            <button
              type="button"
              className="button button--ghost"
              disabled={!selectedPatchId || !canRunSelected || previewBusy || applyBusy}
              onClick={() => runPatch('preview')}
            >
              {previewBusy ? 'Förhandsgranskar...' : 'Kör preview'}
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={!previewData || previewBusy || applyBusy}
              onClick={() => runPatch('apply')}
            >
              {applyBusy ? (hasConflictResults ? 'Skapar pull request...' : 'Applicerar...') : (hasConflictResults ? 'Skapa pull request' : 'Applicera patch')}
            </button>
            <button
              type="button"
              className="button button--ghost"
              disabled={!results.length}
              onClick={copyFailingSites}
            >
              Kopiera felande sajter
            </button>
          </div>

          {actionError ? <p className="feedback feedback--error">{actionError}</p> : null}
          {copyMessage ? <p className="feedback feedback--success">{copyMessage}</p> : null}

          {results.length ? (
            <div className="patch-results">
              <PatchResultSummary results={results} />
              <div className="patch-result-list">
                {results.map((result) => (
                  <PatchResultItem
                    key={`${result.siteId}-${result.patchId}`}
                    result={result}
                    onRemove={() => removePreviewResult(result.siteId, result.patchId)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <details className="patch-accordion patch-history" open>
            <summary className="patch-accordion__summary">
              <div>
                <h3>Senaste patchkörningar</h3>
                <p className="muted-text">Visa och hantera tidigare patchkörningar.</p>
              </div>
            </summary>
            <div className="patch-accordion__content">
              <div className="patch-history__header">
                <button type="button" className="button button--ghost button--small" onClick={refreshRuns}>
                  Uppdatera historik
                </button>
              </div>
              <PatchHistory runs={runs} deletingRunId={deletingRunId} onDelete={deletePatchRun} />
            </div>
          </details>
        </>
      ) : null}
    </section>
  );
}